import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { ENodeTypes, TDocumentTree } from "../types";
import { newApiService } from "../services/api-service";
import { UserContext } from "../../context";

export const CACHE_KEYS = {
  INDEX_BY_YEAR: 'index-by-year',
  BOOKMARKS: 'bookmarks',
  INDEX_BY_MONITOR: 'index-by-monitor',
  READ_LATER: 'read-later',
  LATEST_DOCS: 'latest-docs',
} as const;

export type CacheKeys = typeof CACHE_KEYS;
export type CacheKeyValues = CacheKeys[keyof CacheKeys];

export type TCacheData = {
  data: any,
  isFetching?: boolean,
  isLoading?: boolean,
  expiresAt?: dayjs.Dayjs,
}

const defaultCacheExpirationMs = 60 * 60 * 1000; // 1 hour

const isDocInList = (docid: string, tree: TDocumentTree): boolean => {
  for (const entry of tree) {
    switch (entry.type) {
      case ENodeTypes.LEAF:
        if (entry.data._id === docid) {
          return true;
        }
        break;
      case ENodeTypes.NODE:
        if (isDocInList(docid, entry.children)) return true;
    }
  }
  return false;
}

export type CacheContextType = {
  cache: Record<CacheKeyValues, TCacheData>,
  set: (key: CacheKeyValues, data: any, expiresInMilliseconds?: number) => void,
  get: (key: CacheKeyValues) => TCacheData,
  invalidate: (key: CacheKeyValues) => void,
  isLoading: boolean,
  isBookmarked: (id: string) => boolean | undefined,
  isReadLater: (id: string) => boolean | undefined,
}

export const CacheContext = createContext<CacheContextType | undefined>(undefined);

export const CacheContextProvider = ({ children }: { children: React.ReactNode }) => {
  const { loggedInUser } = useContext(UserContext);
  const isLoggedIn = !!loggedInUser;

  const [cache, setCache] = useState<Record<CacheKeyValues, TCacheData>>(Object.values(CACHE_KEYS).reduce((acc: Record<string, TCacheData>, curr) => { acc[curr] = { data: null, isFetching: true, isLoading: true }; return acc; }, {}));

  const fetchers = useMemo(() => ({
    [CACHE_KEYS.INDEX_BY_YEAR]: newApiService.loadIndexByYearAndTypes,
    [CACHE_KEYS.INDEX_BY_MONITOR]: newApiService.loadIndexByYearAndMonitorNumber,
    [CACHE_KEYS.READ_LATER]: newApiService.loadReadLaterDocs,
    [CACHE_KEYS.BOOKMARKS]: newApiService.loadBookmarkedDocs,
    [CACHE_KEYS.LATEST_DOCS]: newApiService.getLatestDocs,
  }), [])

  const updateCache = useCallback((key: CacheKeyValues, data: any, expiresInMilliseconds?: number) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data,
        isFetching: false,
        isLoading: false,
        expiresAt: expiresInMilliseconds ? dayjs().add(expiresInMilliseconds, 'milliseconds') : undefined
      }
    }));
  }, []);

  const getCache = useCallback((key: CacheKeyValues) => {
    return cache[key];
  }, [cache]);

  const fetchData = useCallback((
    key: CacheKeyValues,
    fetcher: () => Promise<any>,
    firstRun?: boolean,
    expiresInMilliseconds?: number,
  ) => {
    if (!firstRun) {
      setCache(prev => ({
        ...prev,
        [key]: {
          data: null,
          isLoading: false,
          isFetching: true,
        }
      }));
    }

    fetcher()
      .then(data => {
        updateCache(key, data, expiresInMilliseconds)
      });
  }, [setCache, updateCache]);

  const invalidate = useCallback((key: CacheKeyValues) => {
    fetchData(key, fetchers[key], true, defaultCacheExpirationMs)
  }, [fetchData, fetchers])

  const isBookmarked = useCallback((id: string) => {
    if (!cache[CACHE_KEYS.BOOKMARKS].data) return undefined;
    return isDocInList(id, cache[CACHE_KEYS.BOOKMARKS].data);
  }, [cache]);

  const isReadLater = useCallback((id: string) => {
    if (!cache[CACHE_KEYS.READ_LATER].data) return undefined;
    return isDocInList(id, cache[CACHE_KEYS.READ_LATER].data);
  }, [cache]);

  // load data effect — must wait until the user is logged in, otherwise the
  // authenticated endpoints are hit without a token (401) and the cache is
  // permanently filled with empty data until a full page reload.
  useEffect(() => {
    if (!isLoggedIn) return;
    Object.values(CACHE_KEYS).forEach(key => fetchData(key, fetchers[key], true, defaultCacheExpirationMs))
  }, [isLoggedIn, fetchData, fetchers]);

  useEffect(() => {
    const checkData = () => {
      Object.values(CACHE_KEYS).forEach(key => {
        if (!cache[key] || cache[key].isFetching || cache[key].isLoading) return;
        if (!cache[key].expiresAt) return;
        if (cache[key].expiresAt?.isBefore(dayjs())) {
          fetchData(key, fetchers[key], false, defaultCacheExpirationMs)
        }
      })
    }
    const interval = setInterval(() => checkData(), 5 * 1000);
    return () => clearInterval(interval);
  }, [cache, fetchData, fetchers])

  const isLoading = useMemo(() => Object.values(CACHE_KEYS).some((e: CacheKeyValues) => !!cache[e].isLoading), [cache]);

  return (
    <CacheContext.Provider
      value={{
        isLoading, cache, set: updateCache, get: getCache, invalidate,
        isBookmarked, isReadLater,
      }}
    >
      {children}
    </CacheContext.Provider>
  )
}

export function useCache() {
  const ctx = useContext(CacheContext);
  if (!ctx) {
    throw new Error('useCache must be used within a CacheProvider')
  }
  return ctx;
}
